import React from 'react';
import { Box, Button, Container, Typography, IconButton, Stack } from '@mui/material';
import { Instagram, MusicNote, WhatsApp, RestaurantMenu, Favorite } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import BarRockLogo from '../assets/bar-rock-logo.png';

const Home = () => {
  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: '#000000',
        background: 'radial-gradient(circle at center, #1a0205 0%, #000000 70%)', // Subtle red glow center
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          paddingBottom: { xs: 4, md: 0 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <motion.div
            animate={{
              filter: [
                'drop-shadow(0 0 20px rgba(183, 10, 32, 0.2))',
                'drop-shadow(0 0 35px rgba(183, 10, 32, 0.4))',
                'drop-shadow(0 0 20px rgba(183, 10, 32, 0.2))'
              ]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <Box
              component="img"
              src={BarRockLogo}
              alt="Bar Rock Mojito Logo"
              sx={{
                width: '100%',
                maxWidth: { xs: '320px', sm: '450px', md: '600px' },
                maxHeight: { xs: '30vh', sm: '40vh' },
                height: 'auto',
                objectFit: 'contain',
                marginBottom: { xs: 2, md: 4 },
              }}
            />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <Typography
            variant="h5"
            sx={{
              marginBottom: { xs: 4, md: 6 },
              fontWeight: 300,
              color: '#f5f5f5',
              fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' },
              fontStyle: 'italic',
              textShadow: '0 2px 10px rgba(0,0,0,0.8)',
              letterSpacing: '0.08em',
              paddingX: 2,
              opacity: 0.9,
            }}
          >
            "El mejor ambiente, la mejor música y los mejores mojitos."
          </Typography>
        </motion.div>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
            maxWidth: { xs: '320px', md: '400px' }, // More contained width for buttons
            marginTop: 1,
          }}
        >
          {/* Primary Action Button */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <Button
              component={Link}
              to="/rs/fm0924tom"
              variant="contained"
              size="large"
              startIcon={<MusicNote />}
              sx={{
                width: '100%',
                backgroundColor: '#b70a20',
                color: '#fff',
                fontWeight: '700',
                fontSize: '1.1rem',
                padding: '14px 24px',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(183, 10, 32, 0.4)',
                textTransform: 'none',
                letterSpacing: '0.5px',
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': {
                  backgroundColor: '#d32f2f',
                  boxShadow: '0 6px 25px rgba(183, 10, 32, 0.6)',
                },
              }}
            >
              Pedir Canciones
            </Button>
          </motion.div>

          {/* Secondary Action Buttons Container */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            width: '100%'
          }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Button
                component="a"
                href="https://api.whatsapp.com/send/?phone=573157976017&text&type=phone_number&app_absent=0"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsApp />}
                sx={{
                  width: '100%',
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  padding: '12px',
                  borderRadius: '16px',
                  textTransform: 'none',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderColor: '#b70a20',
                    color: '#fff',
                  },
                }}
              >
                Reservar
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Button
                component="a"
                href="https://mojitodiscobar.gpctecnology.com/?fbclid=PAZXh0bgNhZW0BMQABpr8utVG1tc5glvQ96YhhxuNmOGysZ5Ec8gf6PIyRqS5_SnQmcm8X5LYzsQ_aem_AVWBTO8hAxpen3LAOVqFVXYQbAa42IWkqIXf7xnNfTYkbL4GAXzscvAFpsUeQk-_gLM"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<RestaurantMenu />}
                sx={{
                  width: '100%',
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  padding: '12px',
                  borderRadius: '16px',
                  textTransform: 'none',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderColor: '#b70a20',
                    color: '#fff',
                  },
                }}
              >
                Menú
              </Button>
            </motion.div>
          </Box>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <Button
              component="a"
              href="https://docs.google.com/forms/d/e/1FAIpQLScSe3w6PXr12JWUjzyMJZnPntr3t-bhaqnoY4j5vafsWJ4a9g/viewform"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Favorite />}
              sx={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                color: '#fff',
                fontWeight: '600',
                fontSize: '0.9rem',
                padding: '12px',
                borderRadius: '16px',
                textTransform: 'none',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#b70a20',
                  color: '#fff',
                },
              }}
            >
              Únete a Mojilovers
            </Button>
          </motion.div>
        </Box>

        <Box sx={{ marginTop: { xs: 4, md: 6 } }}>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.1, duration: 0.5 }}
          >
            <IconButton
              component="a"
              href="https://www.instagram.com/barrockmojito/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                '&:hover': {
                  color: '#E1306C',
                  transform: 'scale(1.2) rotate(5deg)',
                  background: 'rgba(225, 48, 108, 0.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Instagram sx={{ fontSize: { xs: 32, md: 40 } }} />
            </IconButton>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default Home;
